import KeyboardOutlinedIcon from "@mui/icons-material/KeyboardOutlined";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useLiveQuery } from "@tanstack/react-db";
import { DateTime, Duration } from "luxon";
import React, { Profiler, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Group, Panel, Separator, usePanelRef } from "react-resizable-panels";
import { useNavigate, useParams } from "react-router-dom";

import { updateMembershipConfig } from "@storyboard/web-client";
import { Timings } from "@storyboard/web-logger/timings/Timings";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import FleetMessagingModal from "@/components/FleetMessagingModal";
import { ampli, transport } from "@/config";
import { useDriverFilters, useDriverQuery, useMyDriverUnreadCount } from "@/hooks/driver";
import { buildDriverViewSearchTargets } from "@/hooks/driver/driverSearchTargets";
import type { ColumnSort } from "@/hooks/driver/tagFilters";
import { newlySignaledIds } from "@/hooks/driver/unactivatedHeartbeats";
import { useAvailableGroups } from "@/hooks/driver/useAvailableGroups";
import { useDriverGridState } from "@/hooks/driver/useDriverGridState";
import { useDriverTable } from "@/hooks/driver/useDriverTable";
import { useDriverTags } from "@/hooks/driver/useDriverTags";
import { usePersonalDeviceMembers } from "@/hooks/driver/usePersonalDeviceMembers";
import { useUnactivatedMembers } from "@/hooks/driver/useUnactivatedMembers";
import { ActionContext } from "@/models/ActionsProvider";
import { initialRecipientMode } from "@/models/fleetMessageRecipients";
import { groupsQuery } from "@/models/repositories";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { UserContext } from "@/models/StateProviders/userProvider";
import { useWebSettings } from "@/models/StateProviders/WebSettingsProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { TelemetryContext, actions as telemetryActions } from "@/models/TelemetryProvider";
import { UxContext } from "@/models/UxStateProvider";
import FeedId from "@/routes/workspace/feedId";
import { profilerOnRender } from "@/services/perf/profilerOnRender";
import { recordSpan } from "@/services/telemetry";
import type { DriverRow } from "@/types/driver";
import { applyRangeSelection } from "@/ui/DataTable";
import { zinc } from "@/ui/tokens";
import { type TagFilterState, useColumnFilters } from "./DriverColumnFilters";
import DriverDataGrid from "./DriverDataGrid";
import DriverMetaRow from "./DriverMetaRow";
import DriverToolbar, { DriverTabs, TeamDropdown } from "./DriverToolbar";
import KeyboardShortcutsPopover from "./KeyboardShortcutsPopover";
import { nextHighlightIndex } from "./keyboard/driverShortcuts";
import { type DriverShortcutActions, useDriverShortcuts } from "./keyboard/useDriverShortcuts";
import UnreadMessagesFilterButton from "./UnreadMessagesFilterButton";
import { FeedPanelContext } from "./ui/FeedPanelContext";

const TABLE_PANEL_ID = "driver-table";
const FEED_PANEL_ID = "driver-feed";

/** Stable ref for `useUnactivatedMembers` — inline `["limitedMember"]` would change every render. */
const UNACTIVATED_DRIVER_ROLES: string[] = ["limitedMember"];

/** Stable fallback while a reactive query's first run is pending — `?? []` would change identity every render. */
const NO_ROWS: never[] = [];

/** Composer input id, focused by the `M` shortcut once the feed panel has rendered it. */
const COMPOSER_INPUT_ID = "tts-form-text-input";

/** How long the `M` shortcut waits for the composer to mount before giving up. */
const COMPOSER_FOCUS_TIMEOUT = Duration.fromObject({ seconds: 3 });

/**
 * Steals focus from whatever component currently holds it so keyboard row navigation has a clean table context, and a
 * subsequently-pressed `Enter` opens the highlighted driver's channel instead of activating the previously-focused
 * control (e.g. a toolbar button, the templates button, or the header icon MUI re-focuses after the pane closes).
 *
 * Row-navigation shortcuts never fire while a text field is focused (react-hotkeys-hook's form-tag guard), so the
 * active element here is only ever a button/link/other control — safe to blur to the document body.
 */
function blurActiveElement(): void {
  const el = document.activeElement;
  if (el instanceof HTMLElement && el !== document.body) el.blur();
}

function useTagFilterState(): TagFilterState {
  const [tagFilters, setTagFilters] = useState<Record<string, string[]>>({});
  const [columnSort, setColumnSort] = useState<ColumnSort | null>(null);

  const hasActiveTagFilters = useMemo(() => Object.values(tagFilters).some((v) => v.length > 0), [tagFilters]);

  const toggleTagFilterValue = useCallback((tagId: string, value: string) => {
    setTagFilters((prev) => {
      const current = prev[tagId] ?? [];
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      return { ...prev, [tagId]: next };
    });
  }, []);

  const selectTagFilterValues = useCallback((tagId: string, values: string[]) => {
    setTagFilters((prev) => {
      const current = new Set(prev[tagId] ?? []);
      for (const v of values) current.add(v);
      return { ...prev, [tagId]: [...current] };
    });
  }, []);

  const clearTagFilter = useCallback((tagId: string) => {
    setTagFilters((prev) => {
      const next = { ...prev };
      delete next[tagId];
      return next;
    });
  }, []);

  const clearAllTagFilters = useCallback(() => {
    setTagFilters({});
  }, []);

  return {
    tagFilters,
    toggleTagFilterValue,
    selectTagFilterValues,
    clearTagFilter,
    clearAllTagFilters,
    hasActiveTagFilters,
    columnSort,
    setColumnSort,
  };
}

export default React.memo(function DriverView() {
  const { myAccount } = useContext(MyAccountContext);
  const { setDispatcherFunction } = useContext(UserContext);
  const { currentWorkspaceId: workspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const {
    setFleetMessageModalOpen,
    setFleetMessageActiveChannels,
    setFleetMessageTableSelection,
    setFleetMessageRecipientMode,
    fleetMessageModalOpen,
  } = useContext(UxContext);
  const { trackAction } = useContext(TelemetryContext);
  const { accountEvent } = useContext(ActionContext);
  const navigate = useNavigate();
  const { workspaceMembershipId: activeMembershipId } = useParams();

  const {
    unactivatedMembershipIds,
    unactivatedMembers,
    refetch: refetchUnactivatedMembers,
  } = useUnactivatedMembers(UNACTIVATED_DRIVER_ROLES);
  const unactivatedIds = useMemo(() => [...unactivatedMembershipIds], [unactivatedMembershipIds]);

  const { personalDeviceMembershipIds } = usePersonalDeviceMembers();
  const personalDeviceIds = useMemo(() => [...personalDeviceMembershipIds], [personalDeviceMembershipIds]);

  const { tags, tagValueMap } = useDriverTags();
  const driverViewSearchTargets = useMemo(() => buildDriverViewSearchTargets(tags), [tags]);

  const filters = useDriverFilters(
    workspaceId ?? "",
    myCurrentWorkspaceMembership?.id ?? "",
    myAccount?.preferredLanguage ?? "en",
    unactivatedIds,
    driverViewSearchTargets,
    personalDeviceIds,
  );
  const tagFilterState = useTagFilterState();

  const {
    drivers,
    allAliases,
    totalDriverCount,
    visibleMembershipIds,
    initialLoadPending,
    freshlyHeartbeatingUnactivatedIds,
  } = useDriverQuery(
    workspaceId ?? "",
    myCurrentWorkspaceMembership?.id,
    filters.config,
    tagValueMap,
    tagFilterState.tagFilters,
    tagFilterState.columnSort,
    unactivatedMembers,
    tags,
  );

  // A fresh heartbeat from a member still in the unactivated set means they just logged in (heartbeats only
  // arrive over an authenticated session), so refetch the set immediately instead of waiting out the poll
  // interval — the label, filters, and counts all read from that set. The ref caps it at one refetch per
  // membership per workspace: if the server pathologically keeps reporting the member unactivated, the poll
  // interval is the backstop, not a refetch loop.
  const signaledActivationsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    signaledActivationsRef.current = new Set();
  }, [workspaceId]);
  useEffect(() => {
    const newIds = newlySignaledIds(freshlyHeartbeatingUnactivatedIds, signaledActivationsRef.current);
    if (newIds.length === 0) return;
    for (const id of newIds) signaledActivationsRef.current.add(id);
    refetchUnactivatedMembers();
  }, [freshlyHeartbeatingUnactivatedIds, refetchUnactivatedMembers]);

  const { count: myDriverUnreadCount, initialLoadPending: unreadCountPending } = useMyDriverUnreadCount(
    workspaceId ?? "",
    myCurrentWorkspaceMembership?.id,
    filters.config.teamAliases,
  );

  const batteryRunnable = Boolean(workspaceId && myCurrentWorkspaceMembership?.id);
  const batteryPending = initialLoadPending || unreadCountPending;

  // Perceived driver-list load: mount to the first resolution of the initial query battery.
  const loadStartedAtRef = useRef<Date>(new Date());
  const loadReportedRef = useRef(false);
  useEffect(() => {
    if (loadReportedRef.current || !batteryRunnable || batteryPending) return;
    loadReportedRef.current = true;
    recordSpan("driver_list.load", loadStartedAtRef.current, { driverList: { rows: totalDriverCount } });
  }, [batteryRunnable, batteryPending, totalDriverCount]);

  const gridState = useDriverGridState(workspaceId ?? "");

  const { data: groupQueryRows } = useLiveQuery((q) =>
    workspaceId && myCurrentWorkspaceMembership?.id
      ? groupsQuery(workspaceId, myCurrentWorkspaceMembership.id)(q)
      : null,
  );
  const groupQueryResults = groupQueryRows ?? NO_ROWS;

  const availableGroups = useAvailableGroups(groupQueryResults, filters.groupIds);
  const { getColumnExtra, isFilterActive, getActiveFilterValues, showUnreadOnly, toggleUnreadOnly } = useColumnFilters({
    filters,
    groups: availableGroups,
    tagValueMap,
    visibleMembershipIds,
    tagFilterState,
    tags,
  });

  const filteredDrivers = useMemo(
    () => (showUnreadOnly ? drivers.filter((d) => d.unread) : drivers),
    [drivers, showUnreadOnly],
  );

  // Shared shift-click anchor so the `X` keyboard shortcut and checkbox clicks extend from one selection origin.
  const shiftSelectRef = useRef<string | null>(null);
  // Separate anchor for a Shift+arrow selection run, reset whenever a plain move or `X` starts a fresh selection.
  const rangeAnchorRef = useRef<string | null>(null);

  // Single table instance shared by the grid renderer and the toolbar's column manager.
  const table = useDriverTable({
    drivers: filteredDrivers,
    gridState,
    getColumnExtra,
    isFilterActive,
    getActiveFilterValues,
    showUnreadOnly,
    toggleUnreadOnly,
    tags,
    shiftSelectRef,
    unactivatedMembershipIds,
  });

  const isNeverLoggedInOnly =
    filters.config.statusFilter.length === 1 && filters.config.statusFilter[0] === "never-logged-in";
  /** `driverTotalCount` omits the client-merged unactivated list; for this filter, total must match the roster. */
  const driverMetaTotalCount = isNeverLoggedInOnly ? drivers.length : totalDriverCount;

  const selectedCount = useMemo(
    () => filteredDrivers.filter((d) => gridState.rowSelection[d.workspaceMembershipId]).length,
    [filteredDrivers, gridState.rowSelection],
  );

  const selectedFeedIds = useMemo(
    () =>
      filteredDrivers
        .filter((d) => gridState.rowSelection[d.workspaceMembershipId])
        .map((d) => d.feedId)
        .filter((id): id is string => id != null),
    [filteredDrivers, gridState.rowSelection],
  );

  const handleFleetMessage = useCallback(() => {
    setFleetMessageTableSelection(selectedFeedIds);
    setFleetMessageRecipientMode((mode) => initialRecipientMode(selectedFeedIds, mode));
    setFleetMessageActiveChannels(selectedFeedIds);
    setFleetMessageModalOpen(true);
  }, [
    selectedFeedIds,
    setFleetMessageActiveChannels,
    setFleetMessageModalOpen,
    setFleetMessageTableSelection,
    setFleetMessageRecipientMode,
  ]);

  const handleFleetMessageClose = useCallback(() => {
    gridState.onRowSelectionChange({});
  }, [gridState.onRowSelectionChange]);

  const handleTeamChange = useCallback(
    async (team: string) => {
      setDispatcherFunction?.(team);
      if (workspaceId && myCurrentWorkspaceMembership?.id) {
        try {
          await updateMembershipConfig(transport, {
            workspaceId,
            membershipId: myCurrentWorkspaceMembership.id,
            config: { dispatcherFunction: team },
          });
        } catch {
          /* swallow — optimistic UI already applied */
        }
      }
    },
    [workspaceId, myCurrentWorkspaceMembership?.id, setDispatcherFunction],
  );

  const { feedId } = useParams();
  const feedPanelRef = usePanelRef();
  const { settings, updateWebSettings } = useWebSettings();
  const feedPanelSizes = settings.layout.driverFeedPanels;
  const [feedCollapsed, setFeedCollapsed] = useState(!feedId);

  useEffect(() => {
    if (feedId) {
      const panel = feedPanelRef.current;
      if (panel?.isCollapsed()) panel.expand();
    }
  }, [feedId]);

  const closeFeedPanel = useCallback(() => {
    const panel = feedPanelRef.current;
    if (!panel || panel.isCollapsed()) return;
    panel.collapse();
  }, []);

  const expandFeedPanel = useCallback(() => {
    const panel = feedPanelRef.current;
    if (panel?.isCollapsed()) panel.expand();
  }, []);

  const handleFeedPanelLayout = useCallback(
    (data: Record<string, number>) => {
      const table = data[TABLE_PANEL_ID];
      const feed = data[FEED_PANEL_ID];
      if (table == null || feed == null) return;
      if (feed > 0) updateWebSettings({ layout: { driverFeedPanels: { table, feed } } });
    },
    [updateWebSettings],
  );

  // --- Keyboard shortcuts (WEB-2164) ---
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [rowContextMenuOpen, setRowContextMenuOpen] = useState(false);
  const shortcutsButtonRef = useRef<HTMLButtonElement>(null);

  // The feed panel renders the composer asynchronously after navigation, so `M` polls for it. The pending timer is
  // tracked so a follow-up `M`, focusing search, Escape, or unmount can cancel it instead of stealing focus seconds
  // later.
  const composerFocusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelComposerFocus = useCallback(() => {
    if (composerFocusTimerRef.current) {
      clearTimeout(composerFocusTimerRef.current);
      composerFocusTimerRef.current = null;
    }
  }, []);
  const focusComposerWhenReady = useCallback(() => {
    cancelComposerFocus();
    const deadline = DateTime.now().plus(COMPOSER_FOCUS_TIMEOUT);
    const attempt = () => {
      const input = document.getElementById(COMPOSER_INPUT_ID);
      if (input) {
        input.focus();
        composerFocusTimerRef.current = null;
        return;
      }
      if (DateTime.now() >= deadline) {
        composerFocusTimerRef.current = null;
        return;
      }
      composerFocusTimerRef.current = setTimeout(attempt, Duration.fromObject({ milliseconds: 100 }).toMillis());
    };
    attempt();
  }, [cancelComposerFocus]);
  // The composer auto-focuses on mount (TTSForm `autoFocus`). Opening a channel via `Enter`/`O` must not steal focus,
  // so once the composer mounts and grabs focus, blur it (focus falls back to document body). One blur is enough —
  // autoFocus fires only on mount — and it's cancelled the moment any focus-taking shortcut (`M`, `/`, Escape) or
  // unmount intervenes.
  const suppressComposerFocusWhenReady = useCallback(() => {
    cancelComposerFocus();
    const deadline = DateTime.now().plus(COMPOSER_FOCUS_TIMEOUT);
    const attempt = () => {
      const input = document.getElementById(COMPOSER_INPUT_ID);
      if (input && document.activeElement === input) {
        input.blur();
        composerFocusTimerRef.current = null;
        return;
      }
      if (DateTime.now() >= deadline) {
        composerFocusTimerRef.current = null;
        return;
      }
      composerFocusTimerRef.current = setTimeout(attempt, Duration.fromObject({ milliseconds: 80 }).toMillis());
    };
    attempt();
  }, [cancelComposerFocus]);
  useEffect(() => cancelComposerFocus, [cancelComposerFocus]);

  const highlightedDriver = useMemo(
    () => filteredDrivers.find((d) => d.workspaceMembershipId === highlightedId) ?? null,
    [filteredDrivers, highlightedId],
  );

  // Clear the highlight if its row leaves the list (e.g. toggling the unread filter or marking read while filtered),
  // so the shortcuts don't silently target a row that's no longer visible.
  useEffect(() => {
    if (highlightedId && !filteredDrivers.some((d) => d.workspaceMembershipId === highlightedId)) {
      setHighlightedId(null);
    }
  }, [filteredDrivers, highlightedId]);

  const openShortcuts = useCallback(() => {
    setShortcutsOpen(true);
    ampli.viewKeyboardShortcuts();
  }, []);

  const openDriverChannel = useCallback(
    (driver: DriverRow, opts?: { suppressComposerFocus?: boolean }) => {
      if (driver.feedId == null) return;
      // Diagnostic-tier detail: interaction latency is INP's job and the resulting navigation is measured by
      // feed.navigate; this only ships when the LD aggressive-metrics cohort is on.
      Timings.createEvent("driver-row-click", {
        telemetryCategory: "diagnostic",
        properties: { feedId: driver.feedId, driverName: driver.name },
      });
      trackAction(telemetryActions.feedNavigate(driver.feedId));
      navigate(`/workspaces/${driver.workspaceId}/drivers/${driver.workspaceMembershipId}/channel/${driver.feedId}`);
      expandFeedPanel();
      // Row clicks and `M` let the composer auto-focus; `Enter`/`O` open the channel without stealing focus.
      if (opts?.suppressComposerFocus) suppressComposerFocusWhenReady();
    },
    [navigate, trackAction, expandFeedPanel, suppressComposerFocusWhenReady],
  );

  const moveHighlight = useCallback(
    (delta: 1 | -1) => {
      if (filteredDrivers.length === 0) return;
      blurActiveElement();
      // A plain move ends any Shift+arrow selection run; the next Shift+arrow re-anchors at the current row.
      rangeAnchorRef.current = null;
      const index = highlightedId ? filteredDrivers.findIndex((d) => d.workspaceMembershipId === highlightedId) : -1;
      const next = nextHighlightIndex(index, delta, filteredDrivers.length);
      if (next == null) return;
      setHighlightedId(filteredDrivers[next].workspaceMembershipId);
    },
    [filteredDrivers, highlightedId],
  );

  const extendSelection = useCallback(
    (delta: 1 | -1) => {
      if (filteredDrivers.length === 0) return;
      blurActiveElement();
      const currentIndex = highlightedId
        ? filteredDrivers.findIndex((d) => d.workspaceMembershipId === highlightedId)
        : -1;
      const next = nextHighlightIndex(currentIndex, delta, filteredDrivers.length);
      if (next == null) return;
      const nextId = filteredDrivers[next].workspaceMembershipId;
      // Anchor the run at the current highlight the first time, then grow/shrink it toward the new highlight.
      if (rangeAnchorRef.current === null) rangeAnchorRef.current = highlightedId ?? nextId;
      const anchorId = rangeAnchorRef.current;
      const anchorIndex = filteredDrivers.findIndex((d) => d.workspaceMembershipId === anchorId);
      applyRangeSelection(table, anchorId, nextId);
      // Moving back toward the anchor shrinks the run — deselect the row we just left so it reads like a file list.
      if (
        currentIndex !== -1 &&
        anchorIndex !== -1 &&
        Math.abs(next - anchorIndex) < Math.abs(currentIndex - anchorIndex)
      ) {
        const leftId = filteredDrivers[currentIndex].workspaceMembershipId;
        table.setRowSelection((prev) => {
          const updated = { ...prev };
          delete updated[leftId];
          return updated;
        });
      }
      setHighlightedId(nextId);
    },
    [filteredDrivers, highlightedId, table],
  );

  const focusSearch = useCallback(() => {
    cancelComposerFocus();
    document.getElementById("filterDrivers")?.focus();
  }, [cancelComposerFocus]);

  const toggleChannel = useCallback(() => {
    if (!highlightedDriver) return;
    const isOpen = highlightedDriver.workspaceMembershipId === activeMembershipId && !feedCollapsed;
    if (isOpen) {
      // Mirror the panel-header close, which emits exitFeed before collapsing (see ConnectedPanelHeader).
      ampli.exitFeed({ feedId });
      closeFeedPanel();
    } else {
      // Enter/O open the channel but must not focus the message composer (only clicks and M do).
      openDriverChannel(highlightedDriver, { suppressComposerFocus: true });
    }
  }, [highlightedDriver, activeMembershipId, feedCollapsed, feedId, closeFeedPanel, openDriverChannel]);

  const openComposer = useCallback(() => {
    // Without a feed there's no channel to open — bail before polling, or we'd focus whatever channel is already open.
    if (!highlightedDriver || highlightedDriver.feedId == null) return;
    const isOpen = highlightedDriver.workspaceMembershipId === activeMembershipId && !feedCollapsed;
    if (!isOpen) openDriverChannel(highlightedDriver);
    focusComposerWhenReady();
  }, [highlightedDriver, activeMembershipId, feedCollapsed, openDriverChannel, focusComposerWhenReady]);

  const toggleSelect = useCallback(
    (range: boolean) => {
      if (!highlightedDriver) return;
      const id = highlightedDriver.workspaceMembershipId;
      // `X` starts a fresh selection origin, ending any in-progress Shift+arrow run.
      rangeAnchorRef.current = null;
      if (range && shiftSelectRef.current !== null) {
        applyRangeSelection(table, shiftSelectRef.current, id);
        return;
      }
      table.getRow(id).toggleSelected();
      shiftSelectRef.current = id;
    },
    [highlightedDriver, table],
  );

  const markRead = useCallback(() => {
    if (!highlightedDriver || !accountEvent) return;
    if (highlightedDriver.unread && highlightedDriver.feedId != null) {
      accountEvent("Marked Feed as Read", { feedId: highlightedDriver.feedId });
      if (highlightedDriver.accountId) ampli.markDriverAsRead({ accountGuid: highlightedDriver.accountId });
    }
  }, [highlightedDriver, accountEvent]);

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      cancelComposerFocus();
      if (shortcutsOpen) {
        setShortcutsOpen(false);
        return;
      }
      // Another overlay (fleet-message modal, context menu, popover) already handled this Escape — don't also
      // blur its input or close the feed panel in the same keystroke.
      if (event.defaultPrevented || fleetMessageModalOpen) return;
      const active = document.activeElement as HTMLElement | null;
      if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable)) {
        active.blur();
        return;
      }
      if (feedId && !feedCollapsed) {
        // Match the panel-header / Enter-O close: emit exitFeed before collapsing so keyboard closes are counted.
        ampli.exitFeed({ feedId });
        closeFeedPanel();
        return;
      }
      if (highlightedId) setHighlightedId(null);
    },
    [cancelComposerFocus, shortcutsOpen, fleetMessageModalOpen, feedId, feedCollapsed, closeFeedPanel, highlightedId],
  );

  const shortcutActions: DriverShortcutActions = {
    moveHighlight,
    focusSearch,
    toggleChannel,
    openComposer,
    toggleSelect,
    extendSelection,
    toggleUnread: toggleUnreadOnly,
    fleetMessage: handleFleetMessage,
    markRead,
    showShortcuts: openShortcuts,
    escape: handleEscape,
  };
  useDriverShortcuts(shortcutActions, !shortcutsOpen && !fleetMessageModalOpen && !rowContextMenuOpen);

  const dataGrid = (
    <DriverDataGrid
      table={table}
      drivers={filteredDrivers}
      activeMembershipId={activeMembershipId ?? null}
      columnPinning={gridState.columnPinning}
      onColumnPinningChange={gridState.onColumnPinningChange}
      tags={tags}
      onOpenDriver={openDriverChannel}
      highlightedMembershipId={highlightedId}
      rowSelection={gridState.rowSelection}
      onContextMenuOpenChange={setRowContextMenuOpen}
    />
  );

  const tabs = <DriverTabs filters={filters} myDriverUnreadCount={myDriverUnreadCount} />;

  const toolbar = (
    <DriverToolbar
      filters={filters}
      selectedCount={selectedCount}
      onFleetMessage={handleFleetMessage}
      table={table}
      onColumnPinningChange={gridState.onColumnPinningChange}
      onResetColumns={gridState.resetColumns}
      tags={tags}
      tagFilterState={tagFilterState}
    />
  );

  const unreadFilterButton = <UnreadMessagesFilterButton active={showUnreadOnly} onToggle={toggleUnreadOnly} />;

  const metaRow = (
    <DriverMetaRow
      totalCount={driverMetaTotalCount}
      selectedCount={selectedCount}
      visibleCount={filteredDrivers.length}
    />
  );

  return (
    <Profiler id="DriverView" onRender={profilerOnRender}>
      <Box sx={{ bgcolor: "black", height: "100vh", display: "flex", flexDirection: "column" }}>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Group
            defaultLayout={{
              [TABLE_PANEL_ID]: feedId ? Math.min(feedPanelSizes.table, 65) : 100,
              [FEED_PANEL_ID]: feedId ? Math.max(feedPanelSizes.feed, 35) : 0,
            }}
            onLayoutChanged={handleFeedPanelLayout}
          >
            <Panel id={TABLE_PANEL_ID}>
              <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", pt: 2.5, bgcolor: "black" }}>
                  {tabs}
                  <Box sx={{ flexGrow: 1 }} />
                  <TeamDropdown filters={filters} onTeamChange={handleTeamChange} />
                  <Tooltip title="Keyboard shortcuts">
                    <IconButton
                      ref={shortcutsButtonRef}
                      onClick={openShortcuts}
                      aria-label="Keyboard shortcuts"
                      size="small"
                      sx={{ color: zinc[400], mr: 1, "&:hover": { color: "white" } }}
                    >
                      <KeyboardOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {toolbar}
                {unreadFilterButton}
                {metaRow}
                <Box sx={{ flex: 1, bgcolor: zinc[800], position: "relative", overflow: "hidden" }}>{dataGrid}</Box>
              </Box>
            </Panel>
            <Separator className={`separator ${feedCollapsed ? "hidden" : ""}`} />
            <Panel
              id={FEED_PANEL_ID}
              minSize={500}
              maxSize="80"
              collapsible
              collapsedSize={0}
              panelRef={feedPanelRef}
              onResize={(size) => setFeedCollapsed(size.asPercentage === 0)}
            >
              {feedId && (
                <FeedPanelContext.Provider value={{ closePanel: closeFeedPanel }}>
                  <Box data-sb={TestIds.feed.pane} sx={{ width: "100%", height: "100%", overflow: "hidden" }}>
                    <FeedId key={feedId} type="drivers" />
                  </Box>
                </FeedPanelContext.Provider>
              )}
            </Panel>
          </Group>
        </Box>
      </Box>
      <FleetMessagingModal closeModal={handleFleetMessageClose} />
      <KeyboardShortcutsPopover
        open={shortcutsOpen}
        anchorEl={shortcutsButtonRef.current}
        onClose={() => setShortcutsOpen(false)}
      />
    </Profiler>
  );
});
